import { useMemo } from "react";
import mandatorUtils from "utils/mandators";
import type { CountryCode } from "utils/mandators/config";
import type { BusinessPartner, MandatorKey } from "utils/mandators/types";
import { useUser } from "stores/user";
import { useAllProducts } from "hooks/productHooks/useAllProducts";
import { getMandatorsByProduct } from "hooks/productHooks/utils/filterProductListByMandator";
import { useIsSameProj } from "stores/globalStore";

export const useMandatorOptions = (
  productData?: {
    seriesId: string | number | undefined;
    productName: string | null | undefined;
  } | null,
) => {
  const isSameProj = useIsSameProj();

  const user = useUser();
  const allProducts = useAllProducts();

  const mandatorOptions = useMemo(() => {
    // Only SAME should have mandator options.
    if (!isSameProj) {
      return [];
    }
    try {
      if (!user?.tenant) {
        throw new Error("Tenant does not exist");
      }
      // Ensure tenant provided is valid. Any thrown error will buble up.
      const tenant = mandatorUtils.parseTenant(user.tenant);
      // Get the list of business parters / suppliers that the tenant has access to.
      const businessPartners =
        mandatorUtils.getBusinessPartnersByTenant(tenant);

      // Get an object with all the mandators the tenant has access to.
      // This object then also has all available business parters as its value (by mandator)
      const mandatorToBusinessPartners =
        getMandatorToBusinessPartnersMap(businessPartners);

      const allOptions = Object.entries(mandatorToBusinessPartners).map(
        ([mandator, partners]) => ({
          value: mandatorUtils.parseMandatorKey(mandator),
          label: partners.join(", "),
        }),
      );
      const { seriesId, productName } = productData || {};
      const hasProductData =
        allProducts && seriesId !== undefined && productName;

      if (hasProductData) {
        const optionsFilteredByProduct = getMandatorsByProduct(
          allProducts!,
          seriesId!,
          productName!,
          allOptions,
          user?.tenant,
        );
        return optionsFilteredByProduct;
      }

      return allOptions;
    } catch (error) {
      const typeSafeError =
        error instanceof Error ? error : new Error(String(error));
      console.error(typeSafeError.message);
      return [];
    }
  }, [isSameProj, user, productData, allProducts]);

  return mandatorOptions;
};

const getMandatorToBusinessPartnersMap = (
  businessPartners: BusinessPartner[],
) => {
  const mandators: Record<MandatorKey, CountryCode[]> = {};
  businessPartners.forEach((partner) => {
    const { data: mandator, error: getMandatorError } =
      mandatorUtils.safeRun<MandatorKey>(() =>
        mandatorUtils.getMandatorByCountryCode(partner),
      );
    if (getMandatorError) {
      console.warn(getMandatorError.message);
    } else if (mandator in mandators) {
      mandators[mandator].push(partner);
    } else {
      mandators[mandator] = [partner];
    }
  });

  return mandators;
};
